import React, { useState, useEffect, useRef } from 'react';
import { MeshPhysicalMaterial, Mesh, Material, MeshStandardMaterial, MeshBasicMaterial, TextureLoader, RepeatWrapping } from 'three';
import { loadModel } from './modelHelpers';

const ModelMaterial = ({ 
    url, 
    fileType = null, 
    materialColor, 
    materialMetalness, 
    materialRoughness, 
    materialClearcoat, 
    materialClearcoatRoughness, 
    materialReflectivity, 
    materialTransmission,
    materialEmissive,
    materialEmissiveIntensity,
    materialTextures = {},
    children 
}) => {
    const [isModelLoaded, setIsModelLoaded] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const model = useRef();
    const materials = useRef(null);
    const textureLoader = useRef(new TextureLoader());
    const loadedTextures = useRef({});
    
    // Initialisiere Materialien sofort mit einem Standard-Material
    useEffect(() => {
        if (!materials.current) {
            const defaultMaterial = new MeshPhysicalMaterial({
                color: materialColor || '#ffffff',
                metalness: materialMetalness || 0.3,
                roughness: materialRoughness || 0.7,
                emissive: materialEmissive || '#ffffff',
                emissiveIntensity: materialEmissiveIntensity || 0.2,
                visible: true
            });
            materials.current = [
                new MeshBasicMaterial({ color: "white", toneMapped: false }),
                defaultMaterial
            ];
        }
    }, []);
    
    // Bestimme Dateityp automatisch aus URL falls nicht angegeben
    const getFileType = () => {
        if (fileType) return fileType;
        if (!url) return 'gltf';
        
        // Entferne Query-Parameter und Hash aus URL
        const cleanUrl = url.split('?')[0].split('#')[0];
        const extension = cleanUrl.toLowerCase().split('.').pop();
        
        if (extension === 'glb') return 'glb';
        if (extension === 'gltf') return 'gltf';
        if (extension === 'fbx') return 'fbx';
        if (extension === 'obj') return 'obj';
        return 'gltf'; // Default
    };

    // Lade Texturen
    useEffect(() => {
        const loadTexture = (url, key) => {
            if (!url) {
                if (loadedTextures.current[key]) {
                    loadedTextures.current[key].dispose();
                    delete loadedTextures.current[key];
                }
                return null;
            }

            if (loadedTextures.current[key] && loadedTextures.current[key].userData.url === url) {
                return loadedTextures.current[key];
            }

            // Entlade alte Textur
            if (loadedTextures.current[key]) {
                loadedTextures.current[key].dispose();
            }

            const texture = textureLoader.current.load(url);
            texture.userData.url = url;
            loadedTextures.current[key] = texture;
            return texture;
        };

        // Lade alle Texturen
        const textures = {
            map: materialTextures.albedo ? loadTexture(materialTextures.albedo, 'albedo') : null,
            normalMap: materialTextures.normal ? loadTexture(materialTextures.normal, 'normal') : null,
            roughnessMap: materialTextures.roughness ? loadTexture(materialTextures.roughness, 'roughness') : null,
            metalnessMap: materialTextures.metallic ? loadTexture(materialTextures.metallic, 'metallic') : null,
            aoMap: materialTextures.ao ? loadTexture(materialTextures.ao, 'ao') : null,
            emissiveMap: materialTextures.emissive ? loadTexture(materialTextures.emissive, 'emissive') : null,
        };

        // Erstelle Material mit Texturen
        const materialConfig = {
            color: materialColor,
            metalness: materialMetalness,
            roughness: materialRoughness,
            clearcoat: materialClearcoat,
            clearcoatRoughness: materialClearcoatRoughness,
            reflectivity: materialReflectivity,
            transmission: materialTransmission,
            emissive: materialEmissive || '#000000',
            emissiveIntensity: materialEmissiveIntensity || 0
        };

        // Füge nur vorhandene Texturen hinzu
        if (textures.map) materialConfig.map = textures.map;
        if (textures.normalMap) materialConfig.normalMap = textures.normalMap;
        if (textures.roughnessMap) materialConfig.roughnessMap = textures.roughnessMap;
        if (textures.metalnessMap) materialConfig.metalnessMap = textures.metalnessMap;
        if (textures.aoMap) materialConfig.aoMap = textures.aoMap;
        if (textures.emissiveMap) materialConfig.emissiveMap = textures.emissiveMap;

        const material = new MeshPhysicalMaterial(materialConfig);

        // Setze Textur-Wrap-Modes und andere Eigenschaften
        Object.values(textures).forEach(texture => {
            if (texture) {
                texture.wrapS = RepeatWrapping;
                texture.wrapT = RepeatWrapping;
                texture.flipY = false; // Three.js Texturen werden standardmäßig geflippt
            }
        });

        materials.current = [
            new MeshBasicMaterial({ color: "white", toneMapped: false }),
            material
        ];
    }, [
        materialColor,
        materialMetalness,
        materialRoughness,
        materialClearcoat,
        materialClearcoatRoughness,
        materialReflectivity,
        materialTransmission,
        materialEmissive,
        materialEmissiveIntensity,
        materialTextures.albedo,
        materialTextures.normal,
        materialTextures.roughness,
        materialTextures.metallic,
        materialTextures.ao,
        materialTextures.emissive
    ]);

    useEffect(() => {
        if (!url) return;
        
        setIsModelLoaded(false);
        setLoadError(null);
        
        const actualFileType = getFileType();
        loadModel(url, actualFileType, model)
            .then(() => {
                console.log('ModelMaterial: Model loaded successfully', { url, fileType: actualFileType, model: model.current });
                setIsModelLoaded(true);
                setLoadError(null);
            })
            .catch((error) => {
                console.error('Failed to load model:', error);
                console.error('URL:', url);
                console.error('Detected file type:', actualFileType);
                setIsModelLoaded(false);
                setLoadError(error.message || 'Failed to load model');
            });
    }, [url, fileType]);

    // Stelle sicher, dass Materialien immer vorhanden sind
    if (!materials.current) {
        const defaultMaterial = new MeshPhysicalMaterial({
            color: materialColor || '#ffffff',
            metalness: materialMetalness || 0.3,
            roughness: materialRoughness || 0.7,
            emissive: materialEmissive || '#ffffff',
            emissiveIntensity: materialEmissiveIntensity || 0.2,
            visible: true
        });
        materials.current = [
            new MeshBasicMaterial({ color: "white", toneMapped: false }),
            defaultMaterial
        ];
    }

    if (!isModelLoaded) {
        // Model wird geladen oder es ist ein Fehler aufgetreten
        if (loadError) {
            console.warn('Model loading error:', loadError);
        }
        // Gib leeres Modell zurück während des Ladens, aber mit Materialien
        return children({ model: null, materials: materials.current });
    }

    return children({ model: model.current, materials: materials.current });
};

export default ModelMaterial;
